import type { KLineData } from "klinecharts";
import type { IndicatorInstanceSettings } from "@/lib/chart-indicators/registry";
import { klineLineStyle, dashedValueForStyle } from "@/lib/chart-helpers/indicators-KLine/lineStyle";
import type {
  CompileCandleColorMeta,
  CompileDashboardMeta,
  CompilePlotMeta,
  CompilePriceHistogramMeta,
  CompileResult,
  CompileSignalMeta,
  CompileZoneMeta,
  ComputeResult,
  OhlcBar,
} from "./runtime-types";
import { computeIndicator } from "./runtime/IndicatorRuntimeManager";
import { useIndicatorCompileStore } from "./runtime/compile-store";
import { useIndicatorLoggerStore } from "./runtime/logger-store";
import { getCustomIndicatorIdFromInstanceId } from "./indicator-source";
import { useChartSettingsStore } from "@/lib/chart-settings";

/** Warmup bars recomputed at the prepend boundary for causal indicators. */
const HISTORY_OVERLAP = 1500;
/** Safety valve for persistent render maps (effectively unlimited for normal use). */
const MAX_RENDER_MAP_ENTRIES = 200_000;
/** Debounce window for prepend / full WASM passes during scroll bursts. */
const DEBOUNCED_COMPUTE_MS = 120;

type BarWindowChange =
  | "unchanged"
  | "tailTick"
  | "tailAppend"
  | "historyPrepend"
  | "fullReplace";

/** Metadata for change detection on the full loaded bar list. */
export interface CustomComputeCacheEntry {
  valuesHash: string;
  dataLength: number;
  firstTimestamp?: number;
  lastTimestamp?: number;
  lastBarFingerprint: string;
  prevCalculated: number;
}

interface PersistentRenderState {
  seriesByPlot: Map<string, Map<number, number>>;
}

const computeCache = new Map<string, CustomComputeCacheEntry>();
const persistentRenderByKey = new Map<string, PersistentRenderState>();
const latestDataListByKey = new Map<string, KLineData[]>();
const inFlight = new Map<string, string>();

interface PendingFullReplaceCompute {
  timer: ReturnType<typeof setTimeout>;
  requestKey: string;
  dataList: KLineData[];
  valuesHash: string;
  projectId: string;
  instanceId: string;
  values: Record<string, number | string | boolean>;
  plotKeys: string[];
  cacheKey: string;
}

const pendingFullReplaceCompute = new Map<string, PendingFullReplaceCompute>();

/** Notify chart to refresh after async WASM compute completes (rAF-coalesced). */
let onComputeComplete: (() => void) | null = null;
let refreshScheduled = false;

export function setCustomIndicatorComputeCallback(cb: (() => void) | null): void {
  onComputeComplete = cb;
}

export function historySegmentInstanceId(instanceId: string): string {
  return `${instanceId}:hist`;
}

export async function deinitCustomIndicatorWasmInstances(
  projectId: string,
  instanceId: string,
  inputValues: Record<string, number | string | boolean>
): Promise<void> {
  await runCustomIndicatorLifecycle(projectId, instanceId, inputValues, "deinit");
  await runCustomIndicatorLifecycle(
    projectId,
    historySegmentInstanceId(instanceId),
    inputValues,
    "deinit"
  );
}

function scheduleRefresh(): void {
  if (refreshScheduled || !onComputeComplete) return;
  refreshScheduled = true;
  const run = () => {
    refreshScheduled = false;
    onComputeComplete?.();
  };
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(run);
  } else {
    setTimeout(run, 16);
  }
}

function clearInstanceState(cacheKey?: string, instanceId?: string): void {
  if (!cacheKey && !instanceId) {
    cancelPendingFullReplaceCompute();
    computeCache.clear();
    persistentRenderByKey.clear();
    latestDataListByKey.clear();
    inFlight.clear();
    return;
  }

  const keysToRemove = new Set<string>();
  if (cacheKey) keysToRemove.add(cacheKey);
  if (instanceId) {
    const prefix = `${instanceId}:`;
    for (const key of [
      ...computeCache.keys(),
      ...persistentRenderByKey.keys(),
      ...latestDataListByKey.keys(),
      ...inFlight.keys(),
      ...pendingFullReplaceCompute.keys(),
    ]) {
      if (key === instanceId || key.startsWith(prefix)) keysToRemove.add(key);
    }
  }

  for (const key of keysToRemove) {
    cancelPendingFullReplaceCompute(key);
    computeCache.delete(key);
    persistentRenderByKey.delete(key);
    latestDataListByKey.delete(key);
    inFlight.delete(key);
  }
}

function cancelPendingFullReplaceCompute(cacheKey?: string): void {
  if (!cacheKey) {
    for (const pending of pendingFullReplaceCompute.values()) {
      clearTimeout(pending.timer);
    }
    pendingFullReplaceCompute.clear();
    return;
  }
  const pending = pendingFullReplaceCompute.get(cacheKey);
  if (pending) {
    clearTimeout(pending.timer);
    pendingFullReplaceCompute.delete(cacheKey);
  }
}

/** Drop client compute cache and request chart refresh after a successful compile. */
export function invalidateCustomIndicatorCompile(_projectId: string): void {
  clearInstanceState();
  scheduleRefresh();
}

function detectBarWindowChange(
  cached: CustomComputeCacheEntry | undefined,
  dataList: KLineData[],
  valuesHash: string
): { change: BarWindowChange; startIndex: number } {
  if (!cached || cached.valuesHash !== valuesHash) {
    return { change: "fullReplace", startIndex: 0 };
  }

  const n = dataList.length;
  const firstTs = n > 0 ? dataList[0]?.timestamp : undefined;
  const lastTs = n > 0 ? dataList[n - 1]?.timestamp : undefined;
  const fp = n > 0 ? lastBarFingerprint(dataList[n - 1]!) : "";

  if (
    cached.dataLength === n &&
    cached.firstTimestamp === firstTs &&
    cached.lastTimestamp === lastTs &&
    cached.lastBarFingerprint === fp
  ) {
    return { change: "unchanged", startIndex: 0 };
  }

  if (
    cached.dataLength === n &&
    cached.firstTimestamp === firstTs &&
    cached.lastTimestamp === lastTs &&
    cached.lastBarFingerprint !== fp
  ) {
    return { change: "tailTick", startIndex: Math.max(0, n - 1) };
  }

  if (
    cached.firstTimestamp === firstTs &&
    lastTs != null &&
    cached.lastTimestamp != null &&
    lastTs > cached.lastTimestamp &&
    n > cached.dataLength
  ) {
    return {
      change: "tailAppend",
      startIndex: Math.max(0, cached.prevCalculated - 1),
    };
  }

  if (
    firstTs != null &&
    cached.firstTimestamp != null &&
    firstTs < cached.firstTimestamp &&
    cached.lastTimestamp === lastTs
  ) {
    return { change: "historyPrepend", startIndex: 0 };
  }

  return { change: "fullReplace", startIndex: 0 };
}

function findPrependCount(
  dataList: KLineData[],
  previousFirstTimestamp: number
): number {
  for (let i = 0; i < dataList.length; i++) {
    if (dataList[i]?.timestamp === previousFirstTimestamp) {
      return i;
    }
  }
  return -1;
}

function buildPrependSegment(
  dataList: KLineData[],
  previousFirstTimestamp: number
): KLineData[] | null {
  const prependCount = findPrependCount(dataList, previousFirstTimestamp);
  if (prependCount < 0) return null;
  const end = Math.min(dataList.length, prependCount + HISTORY_OVERLAP);
  return dataList.slice(0, end);
}

function evictOldestFromMap(map: Map<number, number>, maxSize: number): void {
  if (map.size <= maxSize) return;
  const sorted = [...map.keys()].sort((a, b) => a - b);
  const removeCount = map.size - maxSize;
  for (let i = 0; i < removeCount; i++) {
    map.delete(sorted[i]!);
  }
}

function mergeIntoPersistentRender(
  cacheKey: string,
  plotKeys: string[],
  timestamps: number[],
  series: Record<string, (number | null)[]>,
  mergeFromIndex = 0
): Map<string, Map<number, number>> {
  let state = persistentRenderByKey.get(cacheKey);
  if (!state) {
    state = { seriesByPlot: new Map() };
    persistentRenderByKey.set(cacheKey, state);
  }

  const from = Math.max(0, Math.min(mergeFromIndex, timestamps.length));

  for (const key of plotKeys) {
    let map = state.seriesByPlot.get(key);
    if (!map) {
      map = new Map();
      state.seriesByPlot.set(key, map);
    }
    const arr = series[key] ?? [];
    for (let j = from; j < timestamps.length && j < arr.length; j++) {
      const v = arr[j];
      const t = timestamps[j];
      if (t != null && v != null && !Number.isNaN(v)) {
        map.set(t, v);
      }
    }
    evictOldestFromMap(map, MAX_RENDER_MAP_ENTRIES);
  }

  return state.seriesByPlot;
}

function updateCacheAfterMainCompute(
  cacheKey: string,
  dataList: KLineData[],
  valuesHash: string,
  result: ComputeResult,
  plotKeys: string[],
  prevCalculated: number,
  mergeFromIndex = 0
): void {
  const timestamps = dataList.map((b) => b.timestamp);
  computeCache.set(cacheKey, {
    valuesHash,
    dataLength: dataList.length,
    firstTimestamp: timestamps[0],
    lastTimestamp: timestamps[timestamps.length - 1],
    lastBarFingerprint:
      dataList.length > 0
        ? lastBarFingerprint(dataList[dataList.length - 1]!)
        : "",
    prevCalculated,
  });
  mergeIntoPersistentRender(
    cacheKey,
    plotKeys,
    timestamps,
    result.series,
    mergeFromIndex
  );
}

function updateCacheAfterPrepend(
  cacheKey: string,
  cached: CustomComputeCacheEntry,
  dataList: KLineData[],
  segment: KLineData[],
  result: ComputeResult,
  plotKeys: string[]
): void {
  const timestamps = segment.map((b) => b.timestamp);
  computeCache.set(cacheKey, {
    ...cached,
    dataLength: dataList.length,
    firstTimestamp: dataList[0]?.timestamp,
    lastBarFingerprint:
      dataList.length > 0
        ? lastBarFingerprint(dataList[dataList.length - 1]!)
        : cached.lastBarFingerprint,
  });
  mergeIntoPersistentRender(cacheKey, plotKeys, timestamps, result.series);
}

function runComputeNow(args: {
  cacheKey: string;
  requestKey: string;
  projectId: string;
  wasmInstanceId: string;
  values: Record<string, number | string | boolean>;
  bars: KLineData[];
  valuesHash: string;
  plotKeys: string[];
  mode: "full" | "incremental";
  startIndex: number;
  mergeFromIndex?: number;
  onSuccess: (result: ComputeResult) => void;
  onSettled?: () => void;
}): void {
  const {
    cacheKey,
    requestKey,
    projectId,
    wasmInstanceId,
    values,
    bars,
    plotKeys,
    mode,
    startIndex,
    mergeFromIndex = mode === "incremental" ? startIndex : 0,
    onSuccess,
    onSettled,
  } = args;

  if (inFlight.get(cacheKey) === requestKey) return;
  inFlight.set(cacheKey, requestKey);

  void computeIndicator({
    projectId,
    instanceId: wasmInstanceId,
    inputValues: values,
    bars: barsFromKLine(bars),
    lifecycle: "calculate",
    mode,
    startIndex,
  })
    .then((result) => {
      if (!result.success) {
        useIndicatorLoggerStore
          .getState()
          .appendLogger(
            `[compute] FAILED (${plotKeys.length} keys, ${bars.length} bars): ${result.error ?? "unknown"}`
          );
        return;
      }
      onSuccess(result);
      scheduleRefresh();
    })
    .catch((e) => {
      const message = e instanceof Error ? e.message : String(e);
      useIndicatorLoggerStore
        .getState()
        .appendLogger(`[compute] ERROR (${bars.length} bars): ${message}`);
    })
    .finally(() => {
      if (inFlight.get(cacheKey) === requestKey) inFlight.delete(cacheKey);
      onSettled?.();
    });
}

function scheduleFullReplaceDebounced(
  args: Omit<PendingFullReplaceCompute, "timer">
): void {
  const existing = pendingFullReplaceCompute.get(args.cacheKey);
  if (existing) {
    clearTimeout(existing.timer);
    existing.dataList = args.dataList;
    existing.valuesHash = args.valuesHash;
    existing.requestKey = args.requestKey;
    existing.timer = setTimeout(
      () => flushPendingFullReplaceCompute(args.cacheKey),
      DEBOUNCED_COMPUTE_MS
    );
    return;
  }

  pendingFullReplaceCompute.set(args.cacheKey, {
    ...args,
    timer: setTimeout(
      () => flushPendingFullReplaceCompute(args.cacheKey),
      DEBOUNCED_COMPUTE_MS
    ),
  });
}

function flushPendingFullReplaceCompute(cacheKey: string): void {
  const pending = pendingFullReplaceCompute.get(cacheKey);
  if (!pending) return;
  pendingFullReplaceCompute.delete(cacheKey);

  runFullReplaceCompute({
    cacheKey,
    requestKey: pending.requestKey,
    projectId: pending.projectId,
    instanceId: pending.instanceId,
    values: pending.values,
    dataList: pending.dataList,
    valuesHash: pending.valuesHash,
    plotKeys: pending.plotKeys,
  });
}

function runHistoryPrependCompute(args: {
  cacheKey: string;
  requestKey: string;
  projectId: string;
  instanceId: string;
  values: Record<string, number | string | boolean>;
  dataList: KLineData[];
  valuesHash: string;
  plotKeys: string[];
  previousFirstTimestamp: number;
}): void {
  const {
    cacheKey,
    requestKey,
    projectId,
    instanceId,
    values,
    dataList,
    valuesHash,
    plotKeys,
    previousFirstTimestamp,
  } = args;

  if (inFlight.has(cacheKey)) return;

  const cached = computeCache.get(cacheKey);
  if (cached == null) {
    runFullReplaceCompute({
      cacheKey,
      requestKey,
      projectId,
      instanceId,
      values,
      dataList,
      valuesHash,
      plotKeys,
    });
    return;
  }

  const segment = buildPrependSegment(dataList, previousFirstTimestamp);
  if (!segment?.length) {
    runFullReplaceCompute({
      cacheKey,
      requestKey,
      projectId,
      instanceId,
      values,
      dataList,
      valuesHash,
      plotKeys,
    });
    return;
  }

  runComputeNow({
    cacheKey,
    requestKey,
    projectId,
    wasmInstanceId: historySegmentInstanceId(instanceId),
    values,
    bars: segment,
    valuesHash,
    plotKeys,
    mode: "full",
    startIndex: 0,
    mergeFromIndex: 0,
    onSuccess: (result) => {
      updateCacheAfterPrepend(
        cacheKey,
        cached,
        dataList,
        segment,
        result,
        plotKeys
      );
    },
    onSettled: () => {
      maybeRunFollowupHistoryPrepend({
        cacheKey,
        projectId,
        instanceId,
        values,
        plotKeys,
        valuesHash,
      });
    },
  });
}

function maybeRunFollowupHistoryPrepend(args: {
  cacheKey: string;
  projectId: string;
  instanceId: string;
  values: Record<string, number | string | boolean>;
  plotKeys: string[];
  valuesHash: string;
}): void {
  const dataList = latestDataListByKey.get(args.cacheKey);
  if (!dataList?.length) return;

  const cached = computeCache.get(args.cacheKey);
  const currentFirstTs = dataList[0]?.timestamp;
  if (
    cached?.firstTimestamp == null ||
    currentFirstTs == null ||
    currentFirstTs >= cached.firstTimestamp
  ) {
    return;
  }

  const compileRevision =
    useIndicatorCompileStore.getState().getCompileRevision(args.projectId);
  const firstTs = dataList[0]?.timestamp;
  const lastTs = dataList[dataList.length - 1]?.timestamp;
  const requestKey = `${compileRevision}:historyPrepend:${dataList.length}:${firstTs}:${lastTs}:${args.valuesHash}:followup`;

  runHistoryPrependCompute({
    ...args,
    dataList,
    requestKey,
    previousFirstTimestamp: cached.firstTimestamp,
  });
}

function runFullReplaceCompute(args: {
  cacheKey: string;
  requestKey: string;
  projectId: string;
  instanceId: string;
  values: Record<string, number | string | boolean>;
  dataList: KLineData[];
  valuesHash: string;
  plotKeys: string[];
}): void {
  const {
    cacheKey,
    requestKey,
    projectId,
    instanceId,
    values,
    dataList,
    valuesHash,
    plotKeys,
  } = args;

  if (dataList.length === 0) return;

  runComputeNow({
    cacheKey,
    requestKey,
    projectId,
    wasmInstanceId: instanceId,
    values,
    bars: dataList,
    valuesHash,
    plotKeys,
    mode: "full",
    startIndex: 0,
    mergeFromIndex: 0,
    onSuccess: (result) => {
      updateCacheAfterMainCompute(
        cacheKey,
        dataList,
        valuesHash,
        result,
        plotKeys,
        dataList.length,
        0
      );
    },
  });
}

function hashValues(values: Record<string, number | string | boolean>): string {
  return JSON.stringify(values);
}

function lastBarFingerprint(bar: KLineData): string {
  return `${bar.open ?? 0}|${bar.high ?? 0}|${bar.low ?? 0}|${bar.close ?? 0}|${bar.volume ?? 0}`;
}

function barsFromKLine(dataList: KLineData[]): OhlcBar[] {
  return dataList.map((b) => ({
    timestamp: b.timestamp,
    open: b.open ?? 0,
    high: b.high ?? 0,
    low: b.low ?? 0,
    close: b.close ?? 0,
    volume: b.volume ?? 0,
  }));
}

function lineStyleFromPlot(style: string): "solid" | "dashed" | "dotted" {
  const s = style.toLowerCase();
  if (s.includes("dash")) return "dashed";
  if (s.includes("dot")) return "dotted";
  return "solid";
}

function isValidColorValue(value: unknown): value is string {
  if (typeof value !== "string" || !value.trim()) return false;
  const s = value.trim();
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(s)) return true;
  if (/^rgba?\(/i.test(s)) return true;
  return false;
}

function resolveColorInput(
  values: Record<string, number | string | boolean>,
  inputName: string | undefined,
  fallback: string
): string {
  if (!inputName) return fallback;
  const raw = values[inputName];
  return isValidColorValue(raw) ? raw : fallback;
}

function resolvePlotPalette(
  plot: CompilePlotMeta,
  values: Record<string, number | string | boolean>
): string[] {
  const staticColors = plot.colors?.length ? plot.colors : [plot.color];
  if (plot.colorInputs?.length) {
    return plot.colorInputs.map((inputName, i) =>
      resolveColorInput(values, inputName, staticColors[i] ?? plot.color)
    );
  }
  return staticColors;
}

function collectDataKeys(
  plots: CompilePlotMeta[],
  companionKeys?: string[]
): string[] {
  const keys = new Set<string>();
  for (const plot of plots) {
    keys.add(plot.key);
    if (plot.colorKey) keys.add(plot.colorKey);
    if (plot.barStyleKey) keys.add(plot.barStyleKey);
  }
  if (companionKeys) {
    for (const key of companionKeys) {
      if (key) keys.add(key);
    }
  }
  return [...keys];
}

function resolveSignalMetas(meta: CompileResult): CompileSignalMeta[] {
  if (meta.signals?.length) return meta.signals;
  if (meta.signal) return [meta.signal];
  return [];
}

function collectDashboardCompanionKeys(
  dashboard?: CompileDashboardMeta
): string[] {
  if (!dashboard?.cells?.length) return [];
  return dashboard.cells.map((c) => c.key).filter(Boolean);
}

function collectDrawCompanionKeys(
  candleColor?: CompileCandleColorMeta,
  signals?: CompileSignalMeta[],
  zone?: CompileZoneMeta,
  priceHistogram?: CompilePriceHistogramMeta,
  dashboard?: CompileDashboardMeta,
  zones?: CompileZoneMeta[]
): string[] {
  const keys: string[] = [];
  if (candleColor?.stateKey) keys.push(candleColor.stateKey);
  for (const sig of signals ?? []) {
    if (sig.stateKey) keys.push(sig.stateKey);
  }
  keys.push(...collectZoneCompanionKeys(zone, zones));
  if (priceHistogram) {
    if (priceHistogram.buyKey) keys.push(priceHistogram.buyKey);
    if (priceHistogram.sellKey) keys.push(priceHistogram.sellKey);
  }
  keys.push(...collectDashboardCompanionKeys(dashboard));
  return keys;
}

function collectZoneCompanionKeys(
  zone?: CompileZoneMeta,
  zones?: CompileZoneMeta[]
): string[] {
  const list = zones?.length ? zones : zone ? [zone] : [];
  const keys: string[] = [];
  for (const z of list) {
    if (z.stateKey) keys.push(z.stateKey);
    if (z.topKey) keys.push(z.topKey);
    if (z.bottomKey) keys.push(z.bottomKey);
  }
  return keys;
}

function collectPriceHistogramCompanionKeys(
  priceHistogram?: CompilePriceHistogramMeta
): string[] {
  if (!priceHistogram) return [];
  const keys: string[] = [];
  if (priceHistogram.buyKey) keys.push(priceHistogram.buyKey);
  if (priceHistogram.sellKey) keys.push(priceHistogram.sellKey);
  return keys;
}

function collectForegroundCompanionKeys(
  candleColor?: CompileCandleColorMeta,
  signals?: CompileSignalMeta[],
  priceHistogram?: CompilePriceHistogramMeta,
  dashboard?: CompileDashboardMeta
): string[] {
  const keys: string[] = [];
  if (candleColor?.stateKey) keys.push(candleColor.stateKey);
  for (const sig of signals ?? []) {
    if (sig.stateKey) keys.push(sig.stateKey);
  }
  if (priceHistogram) {
    if (priceHistogram.buyKey) keys.push(priceHistogram.buyKey);
    if (priceHistogram.sellKey) keys.push(priceHistogram.sellKey);
  }
  keys.push(...collectDashboardCompanionKeys(dashboard));
  return keys;
}

function readBoolInput(
  values: Record<string, number | string | boolean>,
  inputName: string | undefined,
  defaultValue = true
): boolean {
  if (!inputName) return defaultValue;
  const v = values[inputName];
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return v.toLowerCase() === "true" || v === "1";
  return defaultValue;
}

function readNumberInput(
  values: Record<string, number | string | boolean>,
  inputName: string | undefined,
  defaultValue: number
): number {
  if (!inputName) return defaultValue;
  const v = values[inputName];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const parsed = Number(v);
    if (Number.isFinite(parsed)) return parsed;
  }
  return defaultValue;
}

function parseHexColor(hex: string): [number, number, number] | null {
  const raw = hex.trim().replace(/^#/, "");
  if (raw.length === 3) {
    const r = parseInt(raw[0]! + raw[0]!, 16);
    const g = parseInt(raw[1]! + raw[1]!, 16);
    const b = parseInt(raw[2]! + raw[2]!, 16);
    return [r, g, b];
  }
  if (raw.length === 6) {
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    if ([r, g, b].every((c) => Number.isFinite(c))) return [r, g, b];
  }
  return null;
}

function interpolateHeatPalette(colors: string[], t: number): string {
  const palette = colors.length > 0 ? colors : ["#08010a", "#fcfdbf"];
  if (palette.length === 1) return palette[0]!;
  const clamped = Math.max(0, Math.min(1, t));
  const scaled = clamped * (palette.length - 1);
  const idx = Math.floor(scaled);
  const frac = scaled - idx;
  const c0 = parseHexColor(palette[Math.min(idx, palette.length - 1)]!) ?? [8, 1, 10];
  const c1 = parseHexColor(palette[Math.min(idx + 1, palette.length - 1)]!) ?? c0;
  const r = Math.round(c0[0] + (c1[0] - c0[0]) * frac);
  const g = Math.round(c0[1] + (c1[1] - c0[1]) * frac);
  const b = Math.round(c0[2] + (c1[2] - c0[2]) * frac);
  return `rgb(${r},${g},${b})`;
}

interface PriceVolumeBins {
  buyBins: Float64Array;
  sellBins: Float64Array;
  binCount: number;
  priceMin: number;
  priceMax: number;
}

function buildPriceVolumeBins(
  params: PaneDrawParams,
  buyKey: string,
  sellKey: string,
  bins: number
): PriceVolumeBins | null {
  const { kLineDataList, indicator, visibleRange } = params;
  const from = Math.max(0, visibleRange.from);
  const to = Math.min(kLineDataList.length - 1, visibleRange.to);
  if (from > to) return null;

  let priceMin = Infinity;
  let priceMax = -Infinity;
  for (let i = from; i <= to; i++) {
    const bar = kLineDataList[i];
    if (!bar) continue;
    if (bar.low < priceMin) priceMin = bar.low;
    if (bar.high > priceMax) priceMax = bar.high;
  }

  if (!Number.isFinite(priceMin) || !Number.isFinite(priceMax) || priceMin >= priceMax) {
    return null;
  }

  const binCount = Math.max(8, Math.round(bins));
  const buyBins = new Float64Array(binCount);
  const sellBins = new Float64Array(binCount);
  const range = priceMax - priceMin;

  for (let i = from; i <= to; i++) {
    const bar = kLineDataList[i];
    const row = indicator.result[i];
    if (!bar || !row) continue;

    const buyVol = row[buyKey] ?? 0;
    const sellVol = row[sellKey] ?? 0;
    if (buyVol <= 0 && sellVol <= 0) continue;

    let startBin = Math.floor(((bar.low - priceMin) / range) * binCount);
    let endBin = Math.floor(((bar.high - priceMin) / range) * binCount);
    startBin = Math.max(0, Math.min(binCount - 1, startBin));
    endBin = Math.max(0, Math.min(binCount - 1, endBin));
    if (startBin > endBin) {
      const tmp = startBin;
      startBin = endBin;
      endBin = tmp;
    }

    const span = endBin - startBin + 1;
    const buyPerBin = buyVol / span;
    const sellPerBin = sellVol / span;
    for (let b = startBin; b <= endBin; b++) {
      buyBins[b]! += buyPerBin;
      sellBins[b]! += sellPerBin;
    }
  }

  return { buyBins, sellBins, binCount, priceMin, priceMax };
}

function resolveCandleColorPalette(
  staticColors: string[] | undefined,
  colorInputs: string[] | undefined,
  values: Record<string, number | string | boolean>,
  fallback: string
): string[] {
  const staticList = staticColors?.length ? staticColors : [fallback];
  if (colorInputs?.length) {
    return colorInputs.map((inputName, i) =>
      resolveColorInput(values, inputName, staticList[i] ?? fallback)
    );
  }
  return staticList;
}

interface PaneDrawParams {
  ctx: CanvasRenderingContext2D;
  kLineDataList: KLineData[];
  indicator: { result: Record<string, number | undefined>[] };
  visibleRange: { from: number; to: number };
  barSpace: { halfGapBar: number };
  bounding?: { width: number; height: number };
  xAxis: { convertToPixel: (index: number) => number };
  yAxis: { convertToPixel: (value: number) => number };
}

function resolveDrawBounding(params: PaneDrawParams): { width: number; height: number } {
  if (params.bounding) return params.bounding;
  const canvas = params.ctx.canvas;
  return {
    width: canvas?.width ?? 0,
    height: canvas?.height ?? 0,
  };
}

/** @deprecated alias */
type CandleDrawParams = PaneDrawParams;

function composeDrawCallbacks(
  callbacks: Array<(params: PaneDrawParams) => boolean>
): (params: PaneDrawParams) => boolean {
  return (params) => {
    for (const cb of callbacks) {
      cb(params);
    }
    return false;
  };
}

function drawSignalArrow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  direction: "up" | "down",
  color: string
): void {
  const w = 8;
  const h = 12;
  const isUp = direction === "up";
  const tip = { x, y };
  const left = { x: x - w, y: y + (isUp ? h : -h) };
  const right = { x: x + w, y: y + (isUp ? h : -h) };
  ctx.beginPath();
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(left.x, left.y);
  ctx.lineTo(right.x, right.y);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

function createZoneDrawCallback(args: {
  meta: CompileZoneMeta;
  values: Record<string, number | string | boolean>;
}) {
  const { meta, values } = args;
  const {
    stateKey,
    topKey,
    bottomKey,
    demandColor = "rgba(38,166,154,0.25)",
    supplyColor = "rgba(239,83,80,0.25)",
    brokenColor = "rgba(158,158,158,0.20)",
    demandColorInput,
    supplyColorInput,
    brokenColorInput,
    showInput,
  } = meta;

  const fillDemand = resolveColorInput(values, demandColorInput ?? "", demandColor);
  const fillSupply = resolveColorInput(values, supplyColorInput ?? "", supplyColor);
  const fillBroken = resolveColorInput(values, brokenColorInput ?? "", brokenColor);

  const borderDemand = fillDemand.replace(/[\d.]+\)$/, "0.65)");
  const borderSupply = fillSupply.replace(/[\d.]+\)$/, "0.65)");
  const borderBroken = fillBroken.replace(/[\d.]+\)$/, "0.45)");

  return (params: PaneDrawParams): boolean => {
    if (!readBoolInput(values, showInput, true)) return false;

    const { ctx, indicator, visibleRange, barSpace, xAxis, yAxis } = params;
    const from = Math.max(0, visibleRange.from);
    const to = Math.min(indicator.result.length - 1, visibleRange.to);
    const halfGap = barSpace.halfGapBar;
    const eps = 1e-9;

    let segStart = -1;
    let segTop = 0;
    let segBottom = 0;
    let segState = -1;

    const flushSegment = (endIdx: number) => {
      if (segStart < 0 || segState < 0) return;

      const fill =
        segState === 0 ? fillDemand : segState === 1 ? fillSupply : fillBroken;
      const border =
        segState === 0
          ? borderDemand
          : segState === 1
            ? borderSupply
            : borderBroken;

      const x1 = Math.round(xAxis.convertToPixel(segStart) - halfGap);
      const x2 = Math.round(xAxis.convertToPixel(endIdx) + halfGap);
      const yTop = yAxis.convertToPixel(segTop);
      const yBottom = yAxis.convertToPixel(segBottom);
      const top = Math.min(yTop, yBottom);
      const height = Math.max(1, Math.abs(yBottom - yTop));

      ctx.fillStyle = fill;
      ctx.fillRect(x1, top, x2 - x1, height);
      ctx.strokeStyle = border;
      ctx.lineWidth = 1;
      ctx.strokeRect(x1, top, x2 - x1, height);
    };

    for (let i = from; i <= to; i++) {
      const row = indicator.result[i];
      const stateRaw = row?.[stateKey];
      const topRaw = row?.[topKey];
      const bottomRaw = row?.[bottomKey];

      if (
        stateRaw == null ||
        Number.isNaN(stateRaw) ||
        stateRaw < 0 ||
        topRaw == null ||
        Number.isNaN(topRaw) ||
        bottomRaw == null ||
        Number.isNaN(bottomRaw)
      ) {
        if (segStart >= 0) flushSegment(i - 1);
        segStart = -1;
        continue;
      }

      const state = Math.round(stateRaw);
      const matches =
        segStart >= 0 &&
        state === segState &&
        Math.abs(topRaw - segTop) < eps &&
        Math.abs(bottomRaw - segBottom) < eps;

      if (!matches) {
        if (segStart >= 0) flushSegment(i - 1);
        segStart = i;
        segTop = topRaw;
        segBottom = bottomRaw;
        segState = state;
      }
    }

    if (segStart >= 0) flushSegment(to);
    return false;
  };
}

function createHeatmapDrawCallback(args: {
  meta: CompilePriceHistogramMeta;
  values: Record<string, number | string | boolean>;
}) {
  const { meta, values } = args;
  const {
    buyKey,
    sellKey,
    bins = 60,
    binsInput,
    heatColors = ["#08010a", "#3a0963", "#8c2981", "#de4968", "#fe9f6d", "#fcfdbf"],
    heatMaxOpacity = 0.45,
    heatMaxOpacityInput,
    showHeatmapInput,
  } = meta;

  return (params: PaneDrawParams): boolean => {
    if (!readBoolInput(values, showHeatmapInput, true)) return false;

    const binCount = Math.max(8, Math.round(readNumberInput(values, binsInput, bins)));
    const maxOpacity = readNumberInput(values, heatMaxOpacityInput, heatMaxOpacity);
    const binsData = buildPriceVolumeBins(params, buyKey, sellKey, binCount);
    if (!binsData) return false;

    const { ctx, yAxis } = params;
    const bounding = resolveDrawBounding(params);
    const { buyBins, sellBins, priceMin, priceMax } = binsData;
    const range = priceMax - priceMin;
    let maxTotal = 0;

    for (let b = 0; b < binCount; b++) {
      const total = (buyBins[b] ?? 0) + (sellBins[b] ?? 0);
      if (total > maxTotal) maxTotal = total;
    }
    if (maxTotal <= 0) return false;

    for (let b = 0; b < binCount; b++) {
      const total = (buyBins[b] ?? 0) + (sellBins[b] ?? 0);
      if (total <= 0) continue;

      const intensity = total / maxTotal;
      const binLow = priceMin + (b / binCount) * range;
      const binHigh = priceMin + ((b + 1) / binCount) * range;
      const yTop = yAxis.convertToPixel(binHigh);
      const yBottom = yAxis.convertToPixel(binLow);
      const top = Math.min(yTop, yBottom);
      const height = Math.max(1, Math.abs(yBottom - yTop));

      const rgb = interpolateHeatPalette(heatColors, intensity);
      const alpha = Math.max(0.04, intensity * maxOpacity);
      ctx.fillStyle = rgb.replace("rgb(", "rgba(").replace(")", `,${alpha})`);
      ctx.fillRect(0, top, bounding.width, height);
    }

    return false;
  };
}

function createVolumeProfileDrawCallback(args: {
  meta: CompilePriceHistogramMeta;
  values: Record<string, number | string | boolean>;
}) {
  const { meta, values } = args;
  const {
    buyKey,
    sellKey,
    bins = 60,
    binsInput,
    buyColor = "#2196F3",
    sellColor = "#FFB300",
    buyColorInput,
    sellColorInput,
    profileWidthPct = 28,
    profileWidthPctInput,
    showProfileInput,
  } = meta;

  const colorBuy = resolveColorInput(values, buyColorInput ?? "", buyColor);
  const colorSell = resolveColorInput(values, sellColorInput ?? "", sellColor);

  return (params: PaneDrawParams): boolean => {
    if (!readBoolInput(values, showProfileInput, true)) return false;

    const binCount = Math.max(8, Math.round(readNumberInput(values, binsInput, bins)));
    const widthPct = readNumberInput(values, profileWidthPctInput, profileWidthPct);
    const binsData = buildPriceVolumeBins(params, buyKey, sellKey, binCount);
    if (!binsData) return false;

    const { ctx, yAxis } = params;
    const bounding = resolveDrawBounding(params);
    const { buyBins, sellBins, priceMin, priceMax } = binsData;
    const range = priceMax - priceMin;
    const profileMaxWidth = Math.max(24, bounding.width * (widthPct / 100));
    let maxTotal = 0;

    for (let b = 0; b < binCount; b++) {
      const total = (buyBins[b] ?? 0) + (sellBins[b] ?? 0);
      if (total > maxTotal) maxTotal = total;
    }
    if (maxTotal <= 0) return false;

    const xRight = bounding.width - 1;

    for (let b = 0; b < binCount; b++) {
      const buyVol = buyBins[b] ?? 0;
      const sellVol = sellBins[b] ?? 0;
      const total = buyVol + sellVol;
      if (total <= 0) continue;

      const barWidth = (total / maxTotal) * profileMaxWidth;
      const buyWidth = total > 0 ? (buyVol / total) * barWidth : 0;
      const sellWidth = barWidth - buyWidth;

      const binLow = priceMin + (b / binCount) * range;
      const binHigh = priceMin + ((b + 1) / binCount) * range;
      const yTop = yAxis.convertToPixel(binHigh);
      const yBottom = yAxis.convertToPixel(binLow);
      const top = Math.min(yTop, yBottom);
      const height = Math.max(1, Math.abs(yBottom - yTop));

      const xBuyStart = xRight - buyWidth;
      const xSellStart = xRight - barWidth;

      if (sellWidth > 0) {
        ctx.fillStyle = colorSell;
        ctx.fillRect(xSellStart, top, sellWidth, height);
      }
      if (buyWidth > 0) {
        ctx.fillStyle = colorBuy;
        ctx.fillRect(xBuyStart, top, buyWidth, height);
      }
    }

    return false;
  };
}

function createSignalDrawCallback(args: {
  meta: CompileSignalMeta;
  values: Record<string, number | string | boolean>;
}) {
  const { meta, values } = args;
  const {
    stateKey,
    buyColor = "#26A69A",
    sellColor = "#EF5350",
    buyColorInput,
    sellColorInput,
    buyText = "BUY",
    sellText = "SELL",
    showInput,
    showLabelsInput,
    showPriceInput,
  } = meta;

  const colorBuy = resolveColorInput(values, buyColorInput ?? "", buyColor);
  const colorSell = resolveColorInput(values, sellColorInput ?? "", sellColor);

  return (params: PaneDrawParams): boolean => {
    if (!readBoolInput(values, showInput, true)) return false;

    const showLabels = readBoolInput(values, showLabelsInput, true);
    const showPrices = readBoolInput(values, showPriceInput, false);

    const {
      ctx,
      kLineDataList,
      indicator,
      visibleRange,
      barSpace,
      xAxis,
      yAxis,
    } = params;

    const from = Math.max(0, visibleRange.from);
    const to = Math.min(kLineDataList.length - 1, visibleRange.to);
    const offset = barSpace.halfGapBar + 14;

    ctx.font = "bold 10px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (let i = from; i <= to; i++) {
      const bar = kLineDataList[i];
      if (!bar) continue;

      const stateRaw = indicator.result[i]?.[stateKey];
      if (stateRaw == null || Number.isNaN(stateRaw) || stateRaw < 0) continue;

      const isBuy = Math.round(stateRaw) === 0;
      const color = isBuy ? colorBuy : colorSell;
      const xCenter = Math.round(xAxis.convertToPixel(i));

      if (isBuy) {
        const y = yAxis.convertToPixel(bar.low ?? 0) + offset;
        drawSignalArrow(ctx, xCenter, y, "up", color);
        if (showLabels) {
          ctx.fillStyle = color;
          ctx.fillText(buyText, xCenter, y + 18);
        }
        if (showPrices && bar.close != null) {
          ctx.fillStyle = color;
          ctx.font = "9px sans-serif";
          ctx.fillText(
            bar.close.toFixed(5).replace(/\.?0+$/, ""),
            xCenter,
            y + (showLabels ? 30 : 22)
          );
          ctx.font = "bold 10px sans-serif";
        }
      } else {
        const y = yAxis.convertToPixel(bar.high ?? 0) - offset;
        drawSignalArrow(ctx, xCenter, y, "down", color);
        if (showLabels) {
          ctx.fillStyle = color;
          ctx.fillText(sellText, xCenter, y - 16);
        }
        if (showPrices && bar.close != null) {
          ctx.fillStyle = color;
          ctx.font = "9px sans-serif";
          ctx.fillText(
            bar.close.toFixed(5).replace(/\.?0+$/, ""),
            xCenter,
            y - (showLabels ? 28 : 20)
          );
          ctx.font = "bold 10px sans-serif";
        }
      }
    }

    return false;
  };
}

function createCandleColorDrawCallback(args: {
  stateKey: string;
  bodyPalette: string[];
  borderPalette: string[];
  wickPalette: string[];
}) {
  const { stateKey, bodyPalette, borderPalette, wickPalette } = args;

  return (params: CandleDrawParams): boolean => {
    const chartType = useChartSettingsStore.getState().chartSettings.chartType;
    if (chartType !== "candlesticks") return false;

    const {
      ctx,
      kLineDataList,
      indicator,
      visibleRange,
      barSpace,
      xAxis,
      yAxis,
    } = params;

    const from = Math.max(0, visibleRange.from);
    const to = Math.min(kLineDataList.length - 1, visibleRange.to);

    for (let i = from; i <= to; i++) {
      const bar = kLineDataList[i];
      if (!bar) continue;

      const stateRaw = indicator.result[i]?.[stateKey];
      if (stateRaw == null || Number.isNaN(stateRaw) || stateRaw < 0) continue;

      const stateIdx = Math.round(stateRaw);
      const bodyColor = bodyPalette[stateIdx] ?? bodyPalette[0];
      const borderColor = borderPalette[stateIdx] ?? borderPalette[0];
      const wickColor = wickPalette[stateIdx] ?? wickPalette[0];
      if (!bodyColor) continue;

      const xCenter = Math.round(xAxis.convertToPixel(i));
      const halfWidth = Math.max(1, barSpace.halfGapBar);
      const openY = yAxis.convertToPixel(bar.open ?? 0);
      const closeY = yAxis.convertToPixel(bar.close ?? 0);
      const highY = yAxis.convertToPixel(bar.high ?? 0);
      const lowY = yAxis.convertToPixel(bar.low ?? 0);

      const bodyTop = Math.min(openY, closeY);
      const bodyBottom = Math.max(openY, closeY);
      const bodyHeight = Math.max(1, bodyBottom - bodyTop);

      ctx.beginPath();
      ctx.strokeStyle = wickColor ?? bodyColor;
      ctx.lineWidth = 1;
      ctx.moveTo(xCenter, highY);
      ctx.lineTo(xCenter, lowY);
      ctx.stroke();

      ctx.fillStyle = bodyColor;
      ctx.fillRect(xCenter - halfWidth, bodyTop, halfWidth * 2, bodyHeight);

      ctx.strokeStyle = borderColor ?? bodyColor;
      ctx.lineWidth = 1;
      ctx.strokeRect(xCenter - halfWidth, bodyTop, halfWidth * 2, bodyHeight);
    }

    return false;
  };
}

function readStringInput(
  values: Record<string, number | string | boolean>,
  inputName: string | undefined,
  defaultValue: string
): string {
  if (!inputName) return defaultValue;
  const v = values[inputName];
  if (typeof v === "string" && v.trim()) return v.trim();
  return defaultValue;
}

function resolveDashboardColorPalette(
  staticColors: string[] | undefined,
  colorInputs: string[] | undefined,
  values: Record<string, number | string | boolean>,
  fallback: string
): string[] {
  const staticList = staticColors?.length ? staticColors : [fallback];
  if (colorInputs?.length) {
    return colorInputs.map((inputName, i) =>
      resolveColorInput(values, inputName, staticList[i] ?? fallback)
    );
  }
  return staticList;
}

function readLatestSeriesValue(
  indicator: { result: Record<string, number | undefined>[] },
  key: string,
  fromIndex: number
): number | null {
  for (let i = fromIndex; i >= 0; i--) {
    const v = indicator.result[i]?.[key];
    if (v != null && !Number.isNaN(v)) return v;
  }
  return null;
}

function applyOpacityToColor(color: string, opacity: number): string {
  const clamped = Math.max(0, Math.min(1, opacity));
  if (color.startsWith("rgba(")) {
    return color.replace(
      /rgba\(([^,]+),([^,]+),([^,]+),[^)]+\)/,
      `rgba($1,$2,$3,${clamped})`
    );
  }
  if (color.startsWith("rgb(")) {
    return color.replace("rgb(", "rgba(").replace(")", `,${clamped})`);
  }
  const hex = parseHexColor(color);
  if (hex) {
    return `rgba(${hex[0]},${hex[1]},${hex[2]},${clamped})`;
  }
  return color;
}

function createDashboardDrawCallback(args: {
  meta: CompileDashboardMeta;
  values: Record<string, number | string | boolean>;
}) {
  const { meta, values } = args;
  const cells = [...(meta.cells ?? [])].sort((a, b) => a.row - b.row);

  return (params: PaneDrawParams): boolean => {
    if (!readBoolInput(values, meta.showInput, true)) return false;
    if (cells.length === 0) return false;

    const { ctx, indicator } = params;
    const bounding = resolveDrawBounding(params);
    // Dashboard shows latest computed bar (scanner status), not viewport-local only.
    const fromIndex = Math.max(0, indicator.result.length - 1);

    const position = readStringInput(
      values,
      meta.positionInput,
      meta.position ?? "TopRight"
    );
    const fontSize = Math.max(
      8,
      Math.round(readNumberInput(values, meta.fontSizeInput, meta.fontSize ?? 11))
    );
    const opacity = readNumberInput(values, meta.opacityInput, meta.opacity ?? 0.92);
    const labelWidth = meta.labelWidth ?? 110;
    const valueWidth = meta.valueWidth ?? 110;
    const rowHeight = meta.rowHeight ?? 22;
    const padding = meta.padding ?? 8;
    const margin = meta.margin ?? 12;

    const bgColor = resolveColorInput(
      values,
      meta.backgroundColorInput ?? "",
      meta.backgroundColor ?? "rgba(18,18,24,0.92)"
    );
    const borderColor = resolveColorInput(
      values,
      meta.borderColorInput ?? "",
      meta.borderColor ?? "rgba(255,255,255,0.12)"
    );
    const textColor = resolveColorInput(
      values,
      meta.textColorInput ?? "",
      meta.textColor ?? "#ECEFF1"
    );
    const headerBg = resolveColorInput(
      values,
      meta.headerBackgroundInput ?? "",
      meta.headerBackground ?? "#1565C0"
    );
    const headerText = resolveColorInput(
      values,
      meta.headerTextColorInput ?? "",
      meta.headerTextColor ?? "#FFFFFF"
    );

    const tableWidth = labelWidth + valueWidth;
    const titleHeight = meta.title ? rowHeight + 2 : 0;
    const headers = meta.columnHeaders
      ? meta.columnHeaders.split(",").map((h) => h.trim())
      : [];
    const headerRowHeight = headers.length > 0 ? rowHeight : 0;
    const bodyHeight = cells.length * rowHeight;
    const tableHeight =
      titleHeight + headerRowHeight + bodyHeight + padding * 2;

    let x = margin;
    let y = margin;
    const pos = position.toLowerCase().replace(/[\s_-]/g, "");
    if (pos.includes("right")) {
      x = bounding.width - tableWidth - margin;
    }
    if (pos.includes("bottom")) {
      y = bounding.height - tableHeight - margin;
    }

    ctx.save();
    ctx.globalAlpha = opacity;

    ctx.fillStyle = bgColor;
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const radius = 4;
    const rx = x;
    const ry = y;
    const rw = tableWidth;
    const rh = tableHeight;
    ctx.moveTo(rx + radius, ry);
    ctx.lineTo(rx + rw - radius, ry);
    ctx.quadraticCurveTo(rx + rw, ry, rx + rw, ry + radius);
    ctx.lineTo(rx + rw, ry + rh - radius);
    ctx.quadraticCurveTo(rx + rw, ry + rh, rx + rw - radius, ry + rh);
    ctx.lineTo(rx + radius, ry + rh);
    ctx.quadraticCurveTo(rx, ry + rh, rx, ry + rh - radius);
    ctx.lineTo(rx, ry + radius);
    ctx.quadraticCurveTo(rx, ry, rx + radius, ry);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    let cursorY = y + padding;
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.textBaseline = "middle";

    if (meta.title) {
      ctx.fillStyle = headerBg;
      ctx.fillRect(x + 1, cursorY, tableWidth - 2, rowHeight);
      ctx.fillStyle = headerText;
      ctx.textAlign = "center";
      ctx.fillText(meta.title, x + tableWidth / 2, cursorY + rowHeight / 2);
      cursorY += rowHeight + 2;
    }

    if (headers.length > 0) {
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      ctx.fillRect(x + 1, cursorY, tableWidth - 2, rowHeight);
      ctx.fillStyle = textColor;
      ctx.font = `600 ${Math.max(8, fontSize - 1)}px sans-serif`;
      ctx.textAlign = "left";
      ctx.fillText(headers[0] ?? "", x + 8, cursorY + rowHeight / 2);
      ctx.textAlign = "center";
      ctx.fillText(
        headers[1] ?? "",
        x + labelWidth + valueWidth / 2,
        cursorY + rowHeight / 2
      );
      cursorY += rowHeight;
      ctx.font = `bold ${fontSize}px sans-serif`;
    }

    for (const cell of cells) {
      const raw = readLatestSeriesValue(indicator, cell.key, fromIndex);
      const rowTop = cursorY;
      const isState =
        (cell.mode ?? "State").toLowerCase() !== "numeric" &&
        (cell.texts?.length ?? 0) > 0;

      ctx.fillStyle = "rgba(255,255,255,0.04)";
      ctx.fillRect(x + 1, rowTop, labelWidth - 1, rowHeight);
      ctx.fillStyle = textColor;
      ctx.textAlign = "left";
      ctx.font = `${fontSize}px sans-serif`;
      ctx.fillText(cell.label, x + 8, rowTop + rowHeight / 2);

      let valueText = "—";
      let valueTextColor = textColor;
      let valueBg = "rgba(255,255,255,0.02)";

      if (raw != null) {
        if (isState) {
          const idx = Math.max(0, Math.round(raw));
          const texts = cell.texts ?? [];
          const textPalette = resolveDashboardColorPalette(
            cell.textColors,
            cell.textColorInputs,
            values,
            textColor
          );
          const bgPalette = resolveDashboardColorPalette(
            cell.backgroundColors,
            cell.backgroundColorInputs,
            values,
            "rgba(255,255,255,0.08)"
          );
          valueText = texts[idx] ?? String(idx);
          valueTextColor = textPalette[idx] ?? textPalette[0] ?? textColor;
          valueBg = bgPalette[idx] ?? bgPalette[0] ?? valueBg;
        } else {
          const decimals = cell.decimals ?? 2;
          valueText = `${cell.prefix ?? ""}${raw.toFixed(decimals)}${cell.suffix ?? ""}`;
          valueTextColor = textColor;
        }
      }

      ctx.fillStyle = valueBg;
      ctx.fillRect(x + labelWidth, rowTop, valueWidth - 1, rowHeight);
      ctx.fillStyle = valueTextColor;
      ctx.textAlign = "center";
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.fillText(
        valueText,
        x + labelWidth + valueWidth / 2,
        rowTop + rowHeight / 2
      );

      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(x + labelWidth, rowTop);
      ctx.lineTo(x + labelWidth, rowTop + rowHeight);
      ctx.stroke();

      cursorY += rowHeight;
    }

    ctx.restore();
    return false;
  };
}

interface ResolvedPlotStyle {
  singleColor: string;
  palette: string[];
  lineStyle: "solid" | "dashed" | "dotted";
  width: number;
  colorKey?: string;
}

function resolvePlotStyles(
  plots: CompilePlotMeta[],
  values: Record<string, number | string | boolean>
): ResolvedPlotStyle[] {
  return plots.map((plot) => {
    const palette = resolvePlotPalette(plot, values);
    const singleColor = plot.colorInput
      ? resolveColorInput(values, plot.colorInput, plot.color)
      : palette[0] ?? plot.color;
    return {
      singleColor,
      palette,
      lineStyle: lineStyleFromPlot(plot.style),
      width: plot.width,
      colorKey: plot.colorKey,
    };
  });
}

function isBarPlotStyle(style: string): boolean {
  const s = style.toLowerCase();
  return s.includes("histogram") || s.includes("bar");
}

export const MAIN_PANE_KEY = "__main__";
/** Background layer on candle pane (zones behind candles via zLevel -1). */
export const MAIN_PANE_BG_KEY = "__main_bg__";

export function normalizePlotPaneKey(pane?: string): string {
  const raw = (pane ?? "").trim();
  const lower = raw.toLowerCase();
  if (!raw || lower === "main" || lower === "overlay") return MAIN_PANE_KEY;
  return raw;
}

function groupPlotsByPane(plots: CompilePlotMeta[]): Map<string, CompilePlotMeta[]> {
  const groups = new Map<string, CompilePlotMeta[]>();
  for (const plot of plots) {
    const key = normalizePlotPaneKey(plot.pane);
    const list = groups.get(key) ?? [];
    list.push(plot);
    groups.set(key, list);
  }
  if (groups.size === 0) {
    groups.set(MAIN_PANE_KEY, []);
  }
  return groups;
}

type FigureStyleCallback = (
  data: {
    prev?: { indicatorData?: Record<string, number> };
    current: { indicatorData?: Record<string, number> };
  },
  indicator: unknown,
  defaultStyles: { color?: string; size?: number; style?: string }
) => {
  color: string;
  size?: number;
  style?: "solid" | "dashed" | "dotted" | "fill" | "stroke" | "stroke_fill";
  borderColor?: string;
  dashedValue?: [number, number];
};

function barBodyStyleFromSeries(
  barStyleKey: string | undefined,
  indicatorData: Record<string, number> | undefined
): "fill" | "stroke" {
  if (!barStyleKey || !indicatorData) return "fill";
  const idx = indicatorData[barStyleKey];
  if (idx != null && !Number.isNaN(idx) && Math.round(idx) === 1) {
    return "stroke";
  }
  return "fill";
}

function buildPaneFiguresAndStyles(
  panePlots: CompilePlotMeta[],
  values: Record<string, number | string | boolean>
): {
  figures: Array<{
    key: string;
    title: string;
    type: "line" | "bar";
    baseValue?: number;
    styles?: FigureStyleCallback;
  }>;
  styles: {
    lines?: ReturnType<typeof klineLineStyle>[];
    bars?: Array<{ upColor: string; downColor: string; noChangeColor: string }>;
  };
} {
  const resolved = resolvePlotStyles(panePlots, values);
  const figures: Array<{
    key: string;
    title: string;
    type: "line" | "bar";
    baseValue?: number;
    styles?: FigureStyleCallback;
  }> = [];
  const lineStyles: ReturnType<typeof klineLineStyle>[] = [];
  const barStyles: Array<{
    upColor: string;
    downColor: string;
    noChangeColor: string;
  }> = [];

  panePlots.forEach((plot, plotIndex) => {
    const r = resolved[plotIndex]!;
    const isBar = isBarPlotStyle(plot.style);
    const isColorPalette = r.palette.length > 1 && r.colorKey != null;

    const figure: {
      key: string;
      title: string;
      type: "line" | "bar";
      baseValue?: number;
      styles?: FigureStyleCallback;
    } = {
      key: plot.key,
      title: `${plot.label}: `,
      type: isBar ? "bar" : "line",
    };

    if (plot.baseValue != null && !Number.isNaN(plot.baseValue)) {
      figure.baseValue = plot.baseValue;
    }

    const barStyleKey = plot.barStyleKey;
    if (isColorPalette || (isBar && barStyleKey)) {
      const { palette, colorKey, lineStyle, width } = r;
      const barUpColor = palette[0] ?? plot.color;
      const barDownColor = palette[1] ?? palette[0] ?? plot.color;
      figure.styles = (data) => {
        const ind = data.current.indicatorData;
        const colorIdx =
          colorKey != null
            ? (() => {
                const idx = ind?.[colorKey];
                return idx != null && !Number.isNaN(idx) ? Math.round(idx) : 0;
              })()
            : 0;
        const color = palette[colorIdx] ?? palette[0] ?? plot.color;
        if (isBar) {
          const currV = ind?.[plot.key];
          let barColor = color;
          if (currV != null && !Number.isNaN(currV) && palette.length > 1) {
            barColor = currV >= 0 ? barUpColor : barDownColor;
          }
          const bodyStyle = barBodyStyleFromSeries(barStyleKey, ind);
          return {
            color: barColor,
            style: bodyStyle,
            borderColor: barColor,
          };
        }
        return {
          color,
          size: width,
          style: lineStyle,
          dashedValue: dashedValueForStyle(lineStyle),
        };
      };
    }

    figures.push(figure);

    if (isBar) {
      const upColor = isColorPalette
        ? (r.palette[0] ?? r.singleColor)
        : r.singleColor;
      const downColor = isColorPalette
        ? (r.palette[1] ?? r.palette[0] ?? r.singleColor)
        : r.singleColor;
      barStyles.push({
        upColor,
        downColor,
        noChangeColor: "#888888",
      });
    } else {
      lineStyles.push(
        klineLineStyle(
          isColorPalette
            ? (r.palette[0] ?? r.singleColor)
            : r.singleColor,
          r.width,
          r.lineStyle
        )
      );
    }
  });

  const styles: {
    lines?: ReturnType<typeof klineLineStyle>[];
    bars?: Array<{ upColor: string; downColor: string; noChangeColor: string }>;
  } = {};
  if (lineStyles.length > 0) styles.lines = lineStyles;
  if (barStyles.length > 0) styles.bars = barStyles;
  return { figures, styles };
}

function createCalcForPane(
  projectId: string,
  instanceId: string,
  paneDataKeys: string[],
  scheduleCompute: (dataList: KLineData[]) => void
) {
  return (dataList: KLineData[]) => {
    scheduleCompute(dataList);
    const revision =
      useIndicatorCompileStore.getState().getCompileRevision(projectId);
    const cacheKey = `${instanceId}:r${revision}`;
    const renderMaps = persistentRenderByKey.get(cacheKey)?.seriesByPlot;
    const n = dataList.length;
    const out: Record<string, number | undefined>[] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = {};

    if (!renderMaps) return out;

    for (const key of paneDataKeys) {
      const byTimestamp = renderMaps.get(key);
      if (!byTimestamp) continue;

      for (let i = 0; i < n; i++) {
        const t = dataList[i]?.timestamp;
        if (t == null) continue;
        const v = byTimestamp.get(t);
        if (v != null && !Number.isNaN(v)) {
          out[i] = { ...out[i], [key]: v };
        }
      }
    }
    return out;
  };
}

export interface CustomPaneDescriptor {
  paneKey: string;
  isMain: boolean;
  height?: number;
  minValue?: number;
  maxValue?: number;
  template: Record<string, unknown>;
}

export function createCustomCompiledTemplates(
  displayName: string,
  projectId: string,
  instanceId: string,
  settings: IndicatorInstanceSettings | undefined,
  meta: CompileResult
): CustomPaneDescriptor[] {
  const values = (settings?.values ?? {}) as Record<string, number | string | boolean>;
  const plots = meta.plots ?? [];
  const candleColor = meta.candleColor;
  const signals = resolveSignalMetas(meta);
  const zone = meta.zone;
  const zones = meta.zones;
  const priceHistogram = meta.priceHistogram;
  const dashboard = meta.dashboard;
  const companionKeys = collectDrawCompanionKeys(
    candleColor,
    signals,
    zone,
    priceHistogram,
    dashboard,
    zones
  );
  const allDataKeys = collectDataKeys(plots, companionKeys);
  const plotGroups = groupPlotsByPane(plots);

  const paneConfigByName = new Map(
    (meta.panes ?? []).map((p) => [p.name, p] as const)
  );

  const mainDrawParts: Array<(params: PaneDrawParams) => boolean> = [];

  if (candleColor?.stateKey) {
    const cc = candleColor as CompileCandleColorMeta;
    mainDrawParts.push(
      createCandleColorDrawCallback({
        stateKey: cc.stateKey,
        bodyPalette: resolveCandleColorPalette(
          cc.bodyColors,
          cc.bodyColorInputs,
          values,
          "#26A69A"
        ),
        borderPalette: resolveCandleColorPalette(
          cc.borderColors,
          cc.borderColorInputs,
          values,
          "#1B5E20"
        ),
        wickPalette: resolveCandleColorPalette(
          cc.wickColors,
          cc.wickColorInputs,
          values,
          "#2E7D32"
        ),
      })
    );
  }

  for (const sig of signals) {
    mainDrawParts.push(
      createSignalDrawCallback({
        meta: sig,
        values,
      })
    );
  }

  if (priceHistogram) {
    mainDrawParts.push(
      createVolumeProfileDrawCallback({
        meta: priceHistogram,
        values,
      })
    );
  }

  if (dashboard?.cells?.length) {
    mainDrawParts.push(
      createDashboardDrawCallback({
        meta: dashboard,
        values,
      })
    );
  }

  const mainPaneDraw =
    mainDrawParts.length > 0 ? composeDrawCallbacks(mainDrawParts) : undefined;

  const scheduleCompute = (dataList: KLineData[]) => {
    if (dataList.length === 0) return;

    const compileRevision =
      useIndicatorCompileStore.getState().getCompileRevision(projectId);
    const cacheKey = `${instanceId}:r${compileRevision}`;
    latestDataListByKey.set(cacheKey, dataList);
    const valuesHash = hashValues(values);
    const cached = computeCache.get(cacheKey);

    const { change, startIndex } = detectBarWindowChange(
      cached,
      dataList,
      valuesHash
    );

    if (change === "unchanged") {
      return;
    }

    const firstTs = dataList[0]?.timestamp;
    const lastTs = dataList[dataList.length - 1]?.timestamp;
    const requestKey = `${compileRevision}:${change}:${dataList.length}:${firstTs}:${lastTs}:${valuesHash}:${startIndex}`;

    if (change === "historyPrepend") {
      if (cached?.firstTimestamp == null) {
        scheduleFullReplaceDebounced({
          cacheKey,
          requestKey,
          dataList,
          valuesHash,
          projectId,
          instanceId,
          values,
          plotKeys: allDataKeys,
        });
        return;
      }

      runHistoryPrependCompute({
        cacheKey,
        requestKey,
        projectId,
        instanceId,
        values,
        dataList,
        valuesHash,
        plotKeys: allDataKeys,
        previousFirstTimestamp: cached.firstTimestamp,
      });
      return;
    }

    if (change === "fullReplace") {
      scheduleFullReplaceDebounced({
        cacheKey,
        requestKey,
        dataList,
        valuesHash,
        projectId,
        instanceId,
        values,
        plotKeys: allDataKeys,
      });
      return;
    }

    runComputeNow({
      cacheKey,
      requestKey,
      projectId,
      wasmInstanceId: instanceId,
      values,
      bars: dataList,
      valuesHash,
      plotKeys: allDataKeys,
      mode: "incremental",
      startIndex,
      onSuccess: (result) => {
        updateCacheAfterMainCompute(
          cacheKey,
          dataList,
          valuesHash,
          result,
          allDataKeys,
          dataList.length,
          startIndex
        );
      },
    });
  };

  const descriptors: CustomPaneDescriptor[] = [];

  const bgDrawParts: Array<(params: PaneDrawParams) => boolean> = [];
  if (priceHistogram) {
    bgDrawParts.push(
      createHeatmapDrawCallback({ meta: priceHistogram, values })
    );
  }
  const zoneMetas = zones?.length ? zones : zone ? [zone] : [];
  for (const zoneMeta of zoneMetas) {
    bgDrawParts.push(createZoneDrawCallback({ meta: zoneMeta, values }));
  }

  if (zoneMetas.length > 0 || priceHistogram) {
    const bgKeys = [
      ...collectPriceHistogramCompanionKeys(priceHistogram),
      ...collectZoneCompanionKeys(zone, zones),
    ];
    descriptors.push({
      paneKey: MAIN_PANE_BG_KEY,
      isMain: true,
      template: {
        name: displayName,
        shortName: "",
        zLevel: -1,
        calcParams: [] as unknown[],
        series: "normal" as const,
        figures: [],
        styles: {},
        calc: createCalcForPane(
          projectId,
          instanceId,
          bgKeys,
          scheduleCompute
        ),
        draw:
          bgDrawParts.length > 0
            ? composeDrawCallbacks(bgDrawParts)
            : undefined,
      },
    });
  }

  for (const [paneKey, panePlots] of plotGroups) {
    if (panePlots.length === 0) continue;

    const isMain = paneKey === MAIN_PANE_KEY;
    const paneConfig =
      paneKey !== MAIN_PANE_KEY ? paneConfigByName.get(paneKey) : undefined;

    const paneDataKeys = collectDataKeys(
      panePlots,
      isMain
        ? collectForegroundCompanionKeys(candleColor, signals, priceHistogram, dashboard)
        : undefined
    );
    const { figures, styles } = buildPaneFiguresAndStyles(panePlots, values);

    const template: Record<string, unknown> = {
      name: displayName,
      shortName: displayName,
      calcParams: [] as unknown[],
      series: "normal" as const,
      figures,
      styles,
      calc: createCalcForPane(
        projectId,
        instanceId,
        paneDataKeys,
        scheduleCompute
      ),
    };

    if (isMain && mainPaneDraw) {
      template.draw = mainPaneDraw;
    }

    if (paneConfig?.minValue != null) {
      template.minValue = paneConfig.minValue;
    }
    if (paneConfig?.maxValue != null) {
      template.maxValue = paneConfig.maxValue;
    }

    descriptors.push({
      paneKey,
      isMain,
      height: paneConfig?.height,
      minValue: paneConfig?.minValue,
      maxValue: paneConfig?.maxValue,
      template,
    });
  }

  if (
    mainPaneDraw &&
    !descriptors.some((d) => d.paneKey === MAIN_PANE_KEY)
  ) {
    const paneDataKeys = collectForegroundCompanionKeys(
      candleColor,
      signals,
      priceHistogram,
      dashboard
    );
    const drawOnlyMain: CustomPaneDescriptor = {
      paneKey: MAIN_PANE_KEY,
      isMain: true,
      template: {
        name: displayName,
        shortName: displayName,
        calcParams: [] as unknown[],
        series: "normal" as const,
        figures: [],
        styles: {},
        calc: createCalcForPane(
          projectId,
          instanceId,
          paneDataKeys,
          scheduleCompute
        ),
        draw: mainPaneDraw,
      },
    };
    const bgIndex = descriptors.findIndex(
      (d) => d.paneKey === MAIN_PANE_BG_KEY
    );
    if (bgIndex >= 0) {
      descriptors.splice(bgIndex + 1, 0, drawOnlyMain);
    } else {
      descriptors.unshift(drawOnlyMain);
    }
  }

  return descriptors;
}

/** @deprecated Use createCustomCompiledTemplates — returns the main pane template only. */
export function createCustomCompiledTemplate(
  displayName: string,
  projectId: string,
  instanceId: string,
  settings: IndicatorInstanceSettings | undefined,
  meta: CompileResult
) {
  const panes = createCustomCompiledTemplates(
    displayName,
    projectId,
    instanceId,
    settings,
    meta
  );
  const main =
    panes.find((p) => p.isMain) ?? panes[0];
  return main?.template ?? {
    name: displayName,
    shortName: displayName,
    calcParams: [],
    series: "normal" as const,
    figures: [],
    styles: {},
    calc: () => [],
  };
}

export function getCompiledMetaForProject(projectId: string): CompileResult | undefined {
  return useIndicatorCompileStore.getState().getCompileResult(projectId);
}

export function buildCustomTemplatesIfCompiled(
  indicatorId: string,
  instanceId: string,
  displayName: string,
  settings?: IndicatorInstanceSettings
): CustomPaneDescriptor[] | null {
  const projectId = getCustomIndicatorIdFromInstanceId(indicatorId);
  if (!projectId) return null;
  const meta = getCompiledMetaForProject(projectId);
  if (!meta?.success) return null;
  const hasPlots = (meta.plots?.length ?? 0) > 0;
  const hasMainDraw =
    !!meta.dashboard?.cells?.length ||
    meta.candleColor ||
    meta.signal ||
    (meta.signals?.length ?? 0) > 0 ||
    meta.zone ||
    meta.priceHistogram;
  if (!hasPlots && !hasMainDraw) return null;
  return createCustomCompiledTemplates(
    displayName,
    projectId,
    instanceId,
    settings,
    meta
  );
}

/** @deprecated Use buildCustomTemplatesIfCompiled */
export function buildCustomTemplateIfCompiled(
  indicatorId: string,
  instanceId: string,
  displayName: string,
  settings?: IndicatorInstanceSettings
) {
  const panes = buildCustomTemplatesIfCompiled(
    indicatorId,
    instanceId,
    displayName,
    settings
  );
  if (!panes?.length) return null;
  const main = panes.find((p) => p.isMain) ?? panes[0];
  return main?.template ?? null;
}

export function clearCustomComputeCache(instanceId?: string): void {
  clearInstanceState(undefined, instanceId);
}

export async function runCustomIndicatorLifecycle(
  projectId: string,
  instanceId: string,
  inputValues: Record<string, number | string | boolean>,
  lifecycle: "init" | "deinit"
): Promise<ComputeResult> {
  return computeIndicator({
    projectId,
    instanceId,
    inputValues,
    bars: [],
    lifecycle,
  });
}
